import React, { useEffect, useState } from "react"
import MetaTags from "react-meta-tags"
import { connect } from "react-redux"
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Breadcrumb,
  Row,
  Col,
  Popconfirm,
  Tooltip,
  Badge,
  Statistic,
  Divider,
  Empty,
  Spin,
  Tabs,
} from "antd"
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  BellOutlined,
  SendOutlined,
  EyeOutlined,
  UserOutlined,
  TeamOutlined,
  NotificationOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  AlertOutlined,
  MessageOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SearchOutlined,
  ReloadOutlined,
  PaperClipOutlined,
  FileOutlined,
} from "@ant-design/icons"
import { withTranslation } from "react-i18next"

// Redux actions
import {
  getNotifications,
  addNotification,
  updateNotification,
  deleteNotification,
  resetNotificationFlag,
  getUSERs,
} from "../../store/actions"

import { rolesType } from "../../common/enums/rolesType"
import DocumentFiles, { EntityType } from "../../components/Common/DocumentFiles"
import "./notifications.scss"

const { Option } = Select
const { TextArea } = Input
const { Title, Text } = Typography

// Notification Types
const notificationTypes = [
  { value: 1, label: "اطلاعیه", color: "blue", icon: <NotificationOutlined /> },
  { value: 2, label: "اخبار", color: "cyan", icon: <InfoCircleOutlined /> },
  { value: 3, label: "اخطاریه", color: "orange", icon: <WarningOutlined /> },
  { value: 4, label: "هشدار", color: "red", icon: <AlertOutlined /> },
  { value: 5, label: "پیام", color: "green", icon: <MessageOutlined /> },
  { value: 6, label: "پیام سیستمی", color: "purple", icon: <SettingOutlined /> },
]

// Notification Status
const notificationStatus = [
  { value: 1, label: "ارسال شده", color: "processing", icon: <SendOutlined /> },
  { value: 2, label: "تحویل داده شده", color: "warning", icon: <ClockCircleOutlined /> },
  { value: 3, label: "مشاهده شده", color: "success", icon: <CheckCircleOutlined /> },
]

const Notifications = ({
  t,
  lstNotifications,
  lstUSERs,
  error,
  loading,
  success,
  onGetNotifications,
  onGetUSERs,
  onAddNotification,
  onUpdateNotification,
  onDeleteNotification,
  onResetNotificationFlag,
}) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [filteredData, setFilteredData] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [form] = Form.useForm()
  const [modalVisible, setModalVisible] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [selectedNotification, setSelectedNotification] = useState(null)
  const [viewModalVisible, setViewModalVisible] = useState(false)
  const [viewNotification, setViewNotification] = useState(null)
  const [sendToSpecificUser, setSendToSpecificUser] = useState(false)
  const [selectedRole, setSelectedRole] = useState(null)
  const [filteredUsers, setFilteredUsers] = useState([])
  const [filesModalVisible, setFilesModalVisible] = useState(false)
  const [selectedNotificationForFiles, setSelectedNotificationForFiles] = useState(null)

  useEffect(() => {
    onGetNotifications()
    onGetUSERs()
  }, [])

  useEffect(() => {
    setFilteredData(lstNotifications || [])
    setCurrentPage(1)
  }, [lstNotifications])

  useEffect(() => {
    const filtered = (lstNotifications || []).filter(row => {
      const searchText = searchTerm.toLowerCase()
      const valuesToSearch = [
        row.title,
        row.message,
        row.notificationTypeName,
        row.statusName,
        row.userRole,
      ]
      return valuesToSearch.some(value =>
        String(value || "").toLowerCase().includes(searchText)
      )
    })
    setFilteredData(filtered)
    setCurrentPage(1)
  }, [searchTerm, lstNotifications])

  useEffect(() => {
    if (success || error) {
      setTimeout(() => onResetNotificationFlag(), 3000)
    }
  }, [success, error])

  // Filter users by selected role
  useEffect(() => {
    if (selectedRole && lstUSERs) {
      const users = lstUSERs.filter(user => user.role === selectedRole)
      setFilteredUsers(users)
    } else {
      setFilteredUsers([])
    }
  }, [selectedRole, lstUSERs])

  const handleRoleChange = (value) => {
    setSelectedRole(value)
    form.setFieldsValue({ specificUserId: null })
    setSendToSpecificUser(false)
  }

  const getNotificationTypeConfig = (type) => {
    return notificationTypes.find(t => t.value === type) || notificationTypes[0]
  }

  const getStatusConfig = (status) => {
    return notificationStatus.find(s => s.value === status) || notificationStatus[0]
  }

  // Calculate statistics
  const stats = {
    total: lstNotifications?.length || 0,
    sent: lstNotifications?.filter(n => n.status === 1).length || 0,
    delivered: lstNotifications?.filter(n => n.status === 2).length || 0,
    seen: lstNotifications?.filter(n => n.status === 3).length || 0,
  }

  const columns = [
    {
      title: "عنوان",
      dataIndex: "title",
      key: "title",
      width: 200,
      ellipsis: true,
      render: (text, record) => {
        const typeConfig = getNotificationTypeConfig(record.notificationType)
        return (
          <Space>
            <span style={{ color: typeConfig.color === "blue" ? "#1890ff" : typeConfig.color }}>
              {typeConfig.icon}
            </span>
            <Text strong ellipsis style={{ maxWidth: 150 }}>{text}</Text>
          </Space>
        )
      },
    },
    {
      title: "نوع",
      dataIndex: "notificationTypeName",
      key: "notificationTypeName",
      width: 100,
      render: (text, record) => {
        const typeConfig = getNotificationTypeConfig(record.notificationType)
        return <Tag color={typeConfig.color}>{text}</Tag>
      },
    },
    {
      title: "نقش هدف",
      dataIndex: "userRole",
      key: "userRole",
      width: 120,
      render: (role, record) => {
        const roleInfo = rolesType.find(r => r.key === role)
        return (
          <Space>
            {record.specificUserId ? <UserOutlined /> : <TeamOutlined />}
            <span>{roleInfo?.value || role}</span>
          </Space>
        )
      },
    },
    {
      title: "وضعیت",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status, record) => {
        const statusConfig = getStatusConfig(status)
        return (
          <Tag icon={statusConfig.icon} color={statusConfig.color}>
            {record.statusName}
          </Tag>
        )
      },
    },
    {
      title: "تاریخ ایجاد",
      dataIndex: "createdDate",
      key: "createdDate",
      width: 150,
      render: (date) => {
        if (!date) return "-"
        const d = new Date(date)
        return d.toLocaleDateString("fa-IR")
      },
    },
    {
      title: "فایل‌ها",
      dataIndex: "documentCount",
      key: "documentCount",
      width: 80,
      align: "center",
      render: (count) => (
        <Badge count={count || 0} showZero style={{ backgroundColor: count > 0 ? "#1890ff" : "#d9d9d9" }}>
          <PaperClipOutlined style={{ fontSize: 18, color: "#8c8c8c" }} />
        </Badge>
      ),
    },
    {
      title: "عملیات",
      key: "actions",
      width: 180,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Tooltip title="مشاهده">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            />
          </Tooltip>
          <Tooltip title="فایل‌ها">
            <Badge count={record.documentCount || 0} size="small" offset={[-2, 2]}>
              <Button
                type="text"
                icon={<PaperClipOutlined />}
                onClick={() => handleOpenFilesModal(record)}
              />
            </Badge>
          </Tooltip>
          <Tooltip title="ویرایش">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="آیا از حذف این اطلاعیه مطمئن هستید؟"
            onConfirm={() => onDeleteNotification(record.id)}
            okText="بله"
            cancelText="خیر"
          >
            <Tooltip title="حذف">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const handleSubmit = async values => {
    const notificationData = {
      ...values,
      specificUserId: sendToSpecificUser ? values.specificUserId : null,
    }

    if (editMode && selectedNotification) {
      await onUpdateNotification({ ...notificationData, id: selectedNotification.id })
    } else {
      await onAddNotification(notificationData)
    }

    setModalVisible(false)
    form.resetFields()
    setSendToSpecificUser(false)
    setSelectedRole(null)
  }

  const handleEdit = notification => {
    setEditMode(true)
    setSelectedNotification(notification)
    setSelectedRole(notification.userRole)
    setSendToSpecificUser(!!notification.specificUserId)
    form.setFieldsValue({
      ...notification,
    })
    setModalVisible(true)
  }

  const handleView = notification => {
    setViewNotification(notification)
    setViewModalVisible(true)
  }

  const handleOpenFilesModal = notification => {
    setSelectedNotificationForFiles(notification)
    setFilesModalVisible(true)
  }

  const handleAddNew = () => {
    setEditMode(false)
    setSelectedNotification(null)
    setSendToSpecificUser(false)
    setSelectedRole(null)
    form.resetFields()
    setModalVisible(true)
  }

  return (
    <div className="page-content">
      <MetaTags>
        <title>مدیریت اطلاعیه‌ها | gasUnit</title>
      </MetaTags>

      {/* Header */}
      <div className="mb-4">
        <Row justify="space-between" align="middle">
          <Col>
            <Space direction="vertical" size={0}>
              <Title level={4} style={{ margin: 0 }}>
                <BellOutlined style={{ marginLeft: 8 }} />
                مدیریت اطلاعیه‌ها
              </Title>
              <Breadcrumb
                items={[
                  { title: "داشبورد", href: "/dashboard" },
                  { title: "اطلاعیه‌ها" },
                ]}
              />
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddNew}
              size="middle"
            >
              اطلاعیه جدید
            </Button>
          </Col>
        </Row>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24, marginTop: 8 }}>
        <Col xs={24} sm={12} md={6}>
          <Card
            style={{
              borderRadius: 12,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            <Statistic
              title={<Text style={{ color: "rgba(255,255,255,0.8)" }}>کل اطلاعیه‌ها</Text>}
              value={stats.total}
              prefix={<BellOutlined />}
              valueStyle={{ color: "#fff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            style={{
              borderRadius: 12,
              background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
            }}
          >
            <Statistic
              title={<Text style={{ color: "rgba(255,255,255,0.8)" }}>ارسال شده</Text>}
              value={stats.sent}
              prefix={<SendOutlined />}
              valueStyle={{ color: "#fff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            style={{
              borderRadius: 12,
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
            }}
          >
            <Statistic
              title={<Text style={{ color: "rgba(255,255,255,0.8)" }}>تحویل داده شده</Text>}
              value={stats.delivered}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: "#fff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            style={{
              borderRadius: 12,
              background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
            }}
          >
            <Statistic
              title={<Text style={{ color: "rgba(255,255,255,0.8)" }}>مشاهده شده</Text>}
              value={stats.seen}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#fff" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Alerts */}
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}
      {success && (
        <Alert
          message={success}
          type="success"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Main Card */}
      <Card
        style={{ borderRadius: 12 }}
        title={
          <Space>
            <NotificationOutlined style={{ color: "#1890ff" }} />
            <span>لیست اطلاعیه‌ها</span>
            <Badge count={filteredData.length} style={{ backgroundColor: "#1890ff" }} />
          </Space>
        }
        extra={
          <Space>
            <Input.Search
              placeholder="جستجو..."
              allowClear
              onChange={e => setSearchTerm(e.target.value)}
              style={{ width: 250 }}
              prefix={<SearchOutlined />}
            />
            <Tooltip title="بروزرسانی">
              <Button
                icon={<ReloadOutlined />}
                onClick={() => onGetNotifications()}
                loading={loading}
              />
            </Tooltip>
          </Space>
        }
      >
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={filteredData}
          scroll={{ x: 900 }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            showTotal: (total, range) => `${range[0]}-${range[1]} از ${total} اطلاعیه`,
            onShowSizeChange: (current, size) => {
              setPageSize(size)
              setCurrentPage(1)
            },
            onChange: page => setCurrentPage(page),
            total: filteredData.length,
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="هیچ اطلاعیه‌ای یافت نشد"
              />
            ),
          }}
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={
          <Space>
            {editMode ? <EditOutlined /> : <PlusOutlined />}
            <span>{editMode ? "ویرایش اطلاعیه" : "اطلاعیه جدید"}</span>
          </Space>
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false)
          form.resetFields()
          setEditMode(false)
          setSelectedNotification(null)
          setSendToSpecificUser(false)
          setSelectedRole(null)
        }}
        footer={null}
        destroyOnClose
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ notificationType: 1 }}
        >
          <Form.Item
            label="عنوان"
            name="title"
            rules={[
              { required: true, message: "لطفاً عنوان را وارد کنید" },
              { max: 200, message: "حداکثر 200 کاراکتر" },
            ]}
          >
            <Input placeholder="عنوان اطلاعیه" prefix={<BellOutlined />} />
          </Form.Item>

          <Form.Item
            label="متن پیام"
            name="message"
            rules={[
              { required: true, message: "لطفاً متن پیام را وارد کنید" },
              { max: 1000, message: "حداکثر 1000 کاراکتر" },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="متن اطلاعیه..."
              showCount
              maxLength={1000}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="نوع اطلاعیه"
                name="notificationType"
                rules={[{ required: true, message: "لطفاً نوع را انتخاب کنید" }]}
              >
                <Select placeholder="انتخاب نوع">
                  {notificationTypes.map(type => (
                    <Option key={type.value} value={type.value}>
                      <Space>
                        {type.icon}
                        <span>{type.label}</span>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="نقش کاربری هدف"
                name="userRole"
                rules={[{ required: true, message: "لطفاً نقش را انتخاب کنید" }]}
              >
                <Select
                  placeholder="انتخاب نقش"
                  onChange={handleRoleChange}
                >
                  {rolesType.map(role => (
                    <Option key={role.key} value={role.key}>
                      <Space>
                        <TeamOutlined />
                        <span>{role.value}</span>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {selectedRole && (
            <Form.Item>
              <Space>
                <Text>ارسال به کاربر خاص:</Text>
                <Button
                  type={sendToSpecificUser ? "primary" : "default"}
                  onClick={() => setSendToSpecificUser(!sendToSpecificUser)}
                  icon={sendToSpecificUser ? <UserOutlined /> : <TeamOutlined />}
                >
                  {sendToSpecificUser ? "کاربر خاص" : "همه کاربران با این نقش"}
                </Button>
              </Space>
            </Form.Item>
          )}

          {sendToSpecificUser && selectedRole && (
            <Form.Item
              label="انتخاب کاربر"
              name="specificUserId"
              rules={[{ required: true, message: "لطفاً کاربر را انتخاب کنید" }]}
            >
              <Select
                placeholder="جستجو و انتخاب کاربر..."
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children?.toString() || "").toLowerCase().includes(input.toLowerCase())
                }
              >
                {filteredUsers.map(user => (
                  <Option key={user.id} value={user.id}>
                    {user.firstName} {user.lastName} - {user.email}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Divider />

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ float: "left" }}>
              <Button onClick={() => setModalVisible(false)}>انصراف</Button>
              <Button type="primary" htmlType="submit" icon={<SendOutlined />}>
                {editMode ? "بروزرسانی" : "ارسال"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* View Modal */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            <span>جزئیات اطلاعیه</span>
          </Space>
        }
        open={viewModalVisible}
        onCancel={() => {
          setViewModalVisible(false)
          setViewNotification(null)
        }}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            بستن
          </Button>,
        ]}
        width={800}
      >
        {viewNotification && (
          <Tabs
            defaultActiveKey="1"
            items={[
              {
                key: "1",
                label: (
                  <Space>
                    <InfoCircleOutlined />
                    جزئیات
                  </Space>
                ),
                children: (
                  <div className="notification-view">
                    <Row gutter={[16, 16]}>
                      <Col span={24}>
                        <Card
                          size="small"
                          style={{
                            borderRadius: 8,
                            background: "#f5f5f5",
                          }}
                        >
                          <Space direction="vertical" style={{ width: "100%" }}>
                            <Space>
                              {getNotificationTypeConfig(viewNotification.notificationType).icon}
                              <Title level={5} style={{ margin: 0 }}>
                                {viewNotification.title}
                              </Title>
                            </Space>
                            <Tag color={getNotificationTypeConfig(viewNotification.notificationType).color}>
                              {viewNotification.notificationTypeName}
                            </Tag>
                          </Space>
                        </Card>
                      </Col>

                      <Col span={24}>
                        <Card size="small" style={{ borderRadius: 8 }}>
                          <Text>{viewNotification.message}</Text>
                        </Card>
                      </Col>

                      <Col span={12}>
                        <Text type="secondary">نقش هدف:</Text>
                        <br />
                        <Space>
                          {viewNotification.specificUserId ? <UserOutlined /> : <TeamOutlined />}
                          <Text strong>
                            {rolesType.find(r => r.key === viewNotification.userRole)?.value ||
                              viewNotification.userRole}
                          </Text>
                        </Space>
                      </Col>

                      <Col span={12}>
                        <Text type="secondary">وضعیت:</Text>
                        <br />
                        <Tag
                          icon={getStatusConfig(viewNotification.status).icon}
                          color={getStatusConfig(viewNotification.status).color}
                        >
                          {viewNotification.statusName}
                        </Tag>
                      </Col>

                      <Col span={12}>
                        <Text type="secondary">تاریخ ایجاد:</Text>
                        <br />
                        <Text>
                          {viewNotification.createdDate
                            ? new Date(viewNotification.createdDate).toLocaleDateString("fa-IR")
                            : "-"}
                        </Text>
                      </Col>

                      <Col span={12}>
                        <Text type="secondary">تاریخ مشاهده:</Text>
                        <br />
                        <Text>
                          {viewNotification.seenDate
                            ? new Date(viewNotification.seenDate).toLocaleDateString("fa-IR")
                            : "-"}
                        </Text>
                      </Col>

                      {viewNotification.specificUserId && (
                        <Col span={24}>
                          <Text type="secondary">ارسال به کاربر خاص:</Text>
                          <br />
                          <Tag icon={<UserOutlined />} color="blue">
                            {viewNotification.specificUserId}
                          </Tag>
                        </Col>
                      )}
                    </Row>
                  </div>
                ),
              },
              {
                key: "2",
                label: (
                  <Space>
                    <PaperClipOutlined />
                    فایل‌ها
                    <Badge count={viewNotification.documentCount || 0} style={{ backgroundColor: "#1890ff" }} />
                  </Space>
                ),
                children: (
                  <DocumentFiles
                    entityId={viewNotification.id}
                    entityType={EntityType.Notification}
                    showTitle={false}
                    readOnly={false}
                    maxFiles={5}
                    maxFileSize={5}
                  />
                ),
              },
            ]}
          />
        )}
      </Modal>

      {/* Files Modal */}
      <Modal
        title={
          <Space>
            <PaperClipOutlined />
            مدیریت فایل‌های اطلاعیه
            {selectedNotificationForFiles && (
              <Text type="secondary" style={{ fontSize: 14, fontWeight: "normal" }}>
                ({selectedNotificationForFiles.title})
              </Text>
            )}
          </Space>
        }
        open={filesModalVisible}
        onCancel={() => {
          setFilesModalVisible(false)
          setSelectedNotificationForFiles(null)
          // Refresh notifications to update document count
          onGetNotifications()
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setFilesModalVisible(false)
              setSelectedNotificationForFiles(null)
              onGetNotifications()
            }}
          >
            بستن
          </Button>,
        ]}
        width={700}
        destroyOnClose
      >
        {selectedNotificationForFiles && (
          <DocumentFiles
            entityId={selectedNotificationForFiles.id}
            entityType={EntityType.Notification}
            showTitle={false}
            readOnly={false}
            maxFiles={10}
            maxFileSize={10}
          />
        )}
      </Modal>
    </div>
  )
}

const mapStateToProps = ({ Notifications, USERs }) => ({
  lstNotifications: Notifications.lstNotifications,
  error: Notifications.error,
  success: Notifications.success,
  loading: Notifications.loading,
  lstUSERs: USERs.lstUSERs,
})

const mapDispatchToProps = {
  onGetNotifications: getNotifications,
  onGetUSERs: getUSERs,
  onAddNotification: addNotification,
  onUpdateNotification: updateNotification,
  onDeleteNotification: deleteNotification,
  onResetNotificationFlag: resetNotificationFlag,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(Notifications))
